import React, { useState } from "react";
import { Stack, Typography, Box, Button, useTheme } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useSessionContext } from "../../../contexts/SessionContext";
import logoHeader from "../../../assets/fluxmed-logo-horizontal.png";
import { ContentBoxTitle, PrimaryButton } from "../../../components/common";
import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import axios from "axios";
import { marketplaceService } from "../../../services/marketplace/MarketplaceService";

interface FluxPayPlanProps {
  handleNext: () => void;
  handleBack?: () => void;
}

export function FluxPayPlan({ handleNext, handleBack }: FluxPayPlanProps) {
  const theme = useTheme();
  const { user } = useSessionContext();
  const [loading, setLoading] = useState(false);
  const { access } = useSessionContext();

  const organizationId = localStorage.getItem("organizationValue");

  // Função para exibir mensagem de erro
  const showErrorMessage = (message: string) => {
    alert(message);
  };

  // Função para ativar o FluxPay
  const handleActivateFluxPay = async () => {
    try {
      setLoading(true);
      // Gera um ID de estado único para segurança contra CSRF
      const stateId = uuidv4();

      // Validação prévia do CPF
      if (!user?.username || user.username.length < 11) {
        console.error("CPF/CNPJ inválido", { username: user?.username });
        showErrorMessage(
          "CPF/CNPJ inválido. Verifique seus dados de cadastro."
        );
        return;
      }

      // Verifica se o organizationId está no formato esperado
      if (!organizationId) {
        console.error("ID da organização não encontrado", { organizationId });
        showErrorMessage(
          "Erro de configuração: ID da organização não encontrado."
        );
        return;
      }

      // Log dos dados que serão enviados ao servidor
      console.log("Dados de pré-autorização:", {
        cpf: user?.username,
        organizationId,
        stateId,
      });

      // Registra a intenção de autorização no backend antes do redirecionamento
      await marketplaceService.registerPreAuthorization({
        cpf: user?.username || "",
        organizationId: organizationId.split("-")[1],
        stateId,
        authType: "subscription",
      });

      // Constrói a URL de redirecionamento para o Mercado Pago
      const redirectUri = import.meta.env.VITE_MP_REDIRECT_SPLIT;
      const mpClientId = import.meta.env.VITE_MP_CLIENT_ID_SPLIT;

      // Verifica se as variáveis de ambiente estão definidas
      if (!redirectUri || !mpClientId) {
        console.error("Variáveis de ambiente não configuradas corretamente", {
          redirectUri,
          mpClientId,
        });
        showErrorMessage(
          "Erro de configuração: parâmetros de integração não definidos."
        );
        return;
      }

      // URL completa para autorização no Mercado Pago
      const authorizationUrl = `https://auth.mercadopago.com/authorization?client_id=${mpClientId}&response_type=code&platform_id=mp&redirect_uri=${encodeURIComponent(
        redirectUri
      )}&state=${stateId}`;

      console.log("URL de redirecionamento:", authorizationUrl);

      // Redireciona o usuário para o Mercado Pago
      window.location.href = authorizationUrl;
    } catch (error: any) {
      console.error("Erro ao iniciar processo de autorização:", error);

      // Tratamento de erros mais detalhado
      if (error && error.isAxiosError) {
        console.error("Detalhes do erro da API:", {
          status: error.response?.status,
          data: error.response?.data,
        });

        if (error.response?.data?.message) {
          showErrorMessage(`Erro: ${error.response.data.message}`);
        } else {
          showErrorMessage(
            "Erro na comunicação com o servidor. Tente novamente mais tarde."
          );
        }
      } else {
        showErrorMessage(
          "Ocorreu um erro inesperado. Tente novamente mais tarde."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Função para voltar ao step anterior
  const onBackClick = () => {
    if (handleBack) {
      handleBack();
    }
  };

  return (
    <Stack alignItems="center" spacing={10}>
      <Stack alignItems="center" spacing={2}>
        <img src={logoHeader} alt="Logo fluxmed" width={185} height="auto" />
        <ContentBoxTitle>FluxPay - Pagamento por uso</ContentBoxTitle>
      </Stack>
      <Stack
        spacing={4}
        justifyContent="center"
        alignItems="center"
        sx={{ maxWidth: "700px" }}
      >
        <Typography variant="h6" align="center" fontWeight="500">
          Integração com Mercado Pago para pagamentos flexíveis
        </Typography>

        <Typography variant="body1" align="center">
          O FluxPay é uma solução de pagamento por uso que permite que seus
          pacientes paguem pelos serviços diretamente pelo Mercado Pago, sem
          necessidade de assinatura mensal.
        </Typography>

        <Box
          sx={{
            backgroundColor: "neutral100.main",
            borderRadius: 2,
            p: 3,
            width: "100%",
            border: "1px solid",
            borderColor: "neutral300.main",
          }}
        >
          <Typography variant="body1" fontWeight="500" gutterBottom>
            Requisitos para utilização:
          </Typography>
          <Typography variant="body2" paragraph>
            • É necessário que sua clínica possua uma conta de Pessoa Jurídica
            ativa no Mercado Pago.
          </Typography>
          <Typography variant="body2" paragraph>
            •{" "}
            <a
              href="https://conteudo.mercadopago.com.br/conta-pj-do-mercado-pago"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Veja como criar sua conta pessoa jurídica no MercadoPago
            </a>
          </Typography>

          <Typography
            variant="body1"
            fontWeight="500"
            sx={{ mt: 2 }}
            gutterBottom
          >
            Custos de utilização:
          </Typography>
          <Typography variant="body2" paragraph>
            •{" "}
            <a
              href="https://www.mercadopago.com.br/ajuda/33392"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Confira as taxas de link de pagamentos do Mercado Pago
            </a>
          </Typography>
          <Typography variant="body2" paragraph>
            • Além das taxas de transação do Mercado Pago, você pagará R$ 3,50
            por consulta à Fluxmed.
          </Typography>

          <Typography
            variant="body1"
            fontWeight="500"
            sx={{ mt: 2 }}
            gutterBottom
          >
            Mais informações:
          </Typography>
          <Typography variant="body2">
            •{" "}
            <a
              href="https://projectcore.atlassian.net/wiki/spaces/MDUP/pages/2135785474/FluxPay"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Saiba mais sobre o FluxPay
            </a>
          </Typography>
        </Box>

        <Stack direction="row" spacing={3} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={onBackClick}
            sx={{
              padding: "12px 24px",
              borderRadius: "8px",
              textTransform: "none",
              minWidth: "150px",
              fontSize: "16px",
            }}
          >
            Voltar para a escolha do plano
          </Button>

          <PrimaryButton
            width="250px"
            height="48px"
            onClick={handleActivateFluxPay}
            disabled={loading}
            startIcon={<span style={{ color: "white" }}>💰</span>}
          >
            {loading ? "Carregando..." : "Ativar FluxPay"}
          </PrimaryButton>
        </Stack>
      </Stack>
    </Stack>
  );
}
